/**
 * Wall geometry — straight segments and circular arcs on the floor plane (x, z).
 * (Mirrors server/src/wall_geometry.js)
 */

const EPS = 1e-9;
const clamp01 = (t) => Math.max(0, Math.min(1, t));

export function isArcWall(w) {
  return w?.kind === "arc" && Array.isArray(w.arc?.center) && w.arc.center.length >= 2;
}

export function arcSpec(w) {
  const cx = w.arc.center[0];
  const cz = w.arc.center[1];
  const ax = w.start[0];
  const az = w.start[1];
  const bx = w.end[0];
  const bz = w.end[1];
  const r = Math.hypot(ax - cx, az - cz);
  const a0 = Math.atan2(az - cz, ax - cx);
  const a1 = Math.atan2(bz - cz, bx - cx);
  const clockwise = w.arc.clockwise === true;
  return { cx, cz, r, a0, a1, clockwise };
}

export function arcSweep(a0, a1, clockwise) {
  let d = a1 - a0;
  if (clockwise) {
    while (d > -EPS) d -= 2 * Math.PI;
    while (d <= -2 * Math.PI + EPS) d += 2 * Math.PI;
  } else {
    while (d < EPS) d += 2 * Math.PI;
    while (d >= 2 * Math.PI - EPS) d -= 2 * Math.PI;
  }
  return d;
}

export function wallLength(w) {
  if (!isArcWall(w)) {
    return Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]) || 1;
  }
  const { r, a0, a1, clockwise } = arcSpec(w);
  return Math.abs(arcSweep(a0, a1, clockwise)) * r;
}

export function wallPointAt(w, t) {
  const u = clamp01(t);
  if (!isArcWall(w)) {
    return [w.start[0] + (w.end[0] - w.start[0]) * u, w.start[1] + (w.end[1] - w.start[1]) * u];
  }
  const { cx, cz, r, a0, a1, clockwise } = arcSpec(w);
  const ang = a0 + arcSweep(a0, a1, clockwise) * u;
  return [cx + r * Math.cos(ang), cz + r * Math.sin(ang)];
}

export function wallTangentAt(w, t) {
  if (!isArcWall(w)) {
    const dx = w.end[0] - w.start[0];
    const dz = w.end[1] - w.start[1];
    const len = Math.hypot(dx, dz) || 1;
    return [dx / len, dz / len];
  }
  const { cx, cz, r, a0, a1, clockwise } = arcSpec(w);
  const ang = a0 + arcSweep(a0, a1, clockwise) * clamp01(t);
  const tx = -Math.sin(ang);
  const tz = Math.cos(ang);
  if (clockwise) return [-tx, -tz];
  return [tx, tz];
}

export function paramOnWall(w, px, pz) {
  if (!isArcWall(w)) {
    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    const abx = bx - ax;
    const abz = bz - az;
    const len2 = abx * abx + abz * abz || 1;
    return clamp01(((px - ax) * abx + (pz - az) * abz) / len2);
  }
  const { cx, cz, r, a0, a1, clockwise } = arcSpec(w);
  if (r < EPS) return 0;
  const ang = Math.atan2(pz - cz, px - cx);
  const sweep = arcSweep(a0, a1, clockwise);
  if (Math.abs(sweep) < EPS) return 0;
  let d = ang - a0;
  if (clockwise) {
    while (d > EPS) d -= 2 * Math.PI;
    while (d < sweep - EPS) d += 2 * Math.PI;
  } else {
    while (d < -EPS) d += 2 * Math.PI;
    while (d > sweep + EPS) d -= 2 * Math.PI;
  }
  return clamp01(d / sweep);
}

export function distanceAlongWall(w, px, pz) {
  return paramOnWall(w, px, pz) * wallLength(w);
}

export function sampleWallPolyline(w, maxChord = 0.35) {
  const total = wallLength(w);
  const n = Math.max(2, Math.ceil(total / maxChord));
  const points = [];
  const cum = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    points.push(wallPointAt(w, t));
    cum.push(t * total);
  }
  return { points, cum, length: total };
}

/** Pick arc winding so the arc bulges outward from the room interior. */
export function inferArcClockwise(a, b, center, interiorRef) {
  const cx = center[0];
  const cz = center[1];
  const r = Math.hypot(a[0] - cx, a[1] - cz) || 1;
  const a0 = Math.atan2(a[1] - cz, a[0] - cx);
  const a1 = Math.atan2(b[1] - cz, b[0] - cx);
  const chordMid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const tryCw = () => {
    const ang = a0 + arcSweep(a0, a1, true) / 2;
    return [cx + r * Math.cos(ang), cz + r * Math.sin(ang)];
  };
  const tryCcw = () => {
    const ang = a0 + arcSweep(a0, a1, false) / 2;
    return [cx + r * Math.cos(ang), cz + r * Math.sin(ang)];
  };
  const ref = interiorRef ?? chordMid;
  const dChord = Math.hypot(chordMid[0] - ref[0], chordMid[1] - ref[1]);
  const dCw = Math.hypot(tryCw()[0] - ref[0], tryCw()[1] - ref[1]);
  const dCcw = Math.hypot(tryCcw()[0] - ref[0], tryCcw()[1] - ref[1]);
  return dCw > dChord && dCw >= dCcw;
}

/**
 * True when the floor-plane segment from the camera to an item crosses a solid
 * wall. Door openings are gaps (you can see through them). Windows are not —
 * glass still belongs to the wall, so furniture past a window stays out.
 * A hit within ~8cm of the item is ignored so a piece sitting against the
 * near face of a thick wall is kept.
 */
export function separatedByWall(room, ax, az, bx, bz) {
  const walls = room?.walls;
  if (!Array.isArray(walls) || !walls.length) return false;
  const openings = Array.isArray(room.openings) ? room.openings : [];
  const segLen = Math.hypot(bx - ax, bz - az);
  if (segLen < 1e-4) return false;
  for (const wall of walls) {
    if (!Array.isArray(wall?.start) || !Array.isArray(wall?.end)) continue;
    const chords = wallChords(wall);
    for (let i = 0; i < chords.length; i++) {
      const [cx, cz, dx, dz] = chords[i];
      const hit = segmentCross(ax, az, bx, bz, cx, cz, dx, dz, segLen);
      if (!hit) continue;
      if (crossingIsDoor(wall, hit.x, hit.z, openings)) continue;
      return true;
    }
  }
  return false;
}

function wallChords(wall) {
  if (!isArcWall(wall)) {
    return [[wall.start[0], wall.start[1], wall.end[0], wall.end[1]]];
  }
  const n = Math.max(8, Math.ceil(wallLength(wall) / 0.35));
  const chords = [];
  let prev = wallPointAt(wall, 0);
  for (let i = 1; i <= n; i++) {
    const p = wallPointAt(wall, i / n);
    chords.push([prev[0], prev[1], p[0], p[1]]);
    prev = p;
  }
  return chords;
}

function segmentCross(ax, az, bx, bz, cx, cz, dx, dz, segLen) {
  const rx = bx - ax;
  const rz = bz - az;
  const sx = dx - cx;
  const sz = dz - cz;
  const den = rx * sz - rz * sx;
  if (Math.abs(den) < 1e-8) return null;
  const qx = cx - ax;
  const qz = cz - az;
  const t = (qx * sz - qz * sx) / den;
  const u = (qx * rz - qz * rx) / den;
  const tMin = 0.04 / segLen;
  const tMax = 1 - 0.08 / segLen;
  if (t <= tMin || t >= tMax || u <= 0.002 || u >= 0.998) return null;
  return { x: ax + t * rx, z: az + t * rz };
}

function crossingIsDoor(wall, x, z, openings) {
  if (wall.id == null) return false;
  const len = wallLength(wall);
  const u = paramOnWall(wall, x, z);
  for (const op of openings) {
    if (!op || op.wall !== wall.id) continue;
    if (!String(op.type || "").startsWith("door")) continue;
    if (!Array.isArray(op.center) || op.center.length < 3) continue;
    const width = Number(op.width) || 0.9;
    const uc = paramOnWall(wall, op.center[0], op.center[2]);
    if (Math.abs(u - uc) * len <= width / 2 + 0.06) return true;
  }
  return false;
}
